import { Text } from 'components';
import { formatLocale, theme } from 'lib';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Cell, Row } from './GridTable';

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => p.bg};
  color: ${(p) => p.col};
  white-space: nowrap;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
`;

// Colours only. The label is looked up from `admin.tiri.modules.<id>` at render, so a
// module-level constant can't freeze it at the language that was active when this module loaded.
const MODULE_STYLES = {
  tagit: { bg: '#E8F5E9', col: '#2E7D32' },
  rateit: { bg: '#EDE7F6', col: '#4527A0' }
};

const ListItem = memo(({ item, isSelected, onClick, offsetTop }) => {
  const { t } = useTranslation();

  // A photo belongs to both modules on this one row, so this is a set of badges rather than a
  // single type cell.
  const modules = item.modules || [];
  const isTaggable = modules.includes('tagit');

  // Each module's working state is namespaced under its own key on the Tiri row, so the modules
  // can't collide over one flag or counter. Destructured once here rather than optional-chained
  // at each of the three cells below. A row always has it, but default it anyway — this list is
  // the one screen that shows Rate It-only rows too.
  const { category, categoryStatus, isPoolEligible } = item.tagit || {};

  // `media.type` is the FancamMediaType enum ('video' | 'image'), and the backend defaults it
  // to video — so anything that isn't explicitly an image reads as a video, and a row with no
  // media at all gets a dash rather than a wrong label.
  const mediaLabel = !item.media
    ? '-'
    : t(`admin.tiri.mediaTypes.${item.media.type === 'image' ? 'image' : 'video'}`);

  // formatLocale builds a Date and reads the store for the active locale on every call, so the
  // date is formatted once per row rather than once per render.
  const createdAt = useMemo(
    () => (item.createdAt ? formatLocale(new Date(item.createdAt), 'LLL do yyy, hh:mm a') : '-'),
    [item.createdAt]
  );

  return (
    <Row
      data-tiri-row
      onClick={() => onClick(item._id)}
      isSelected={isSelected}
      // Positioned by the virtualizer rather than by flow, so recycling a row is a transform
      // rather than a reflow of everything below it.
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${offsetTop}px)`
      }}
    >
      <Cell>
        {/* The media kind as a word rather than the thumbnail itself. A row only needs to say
            what sort of content it is — opening it shows the media — and every thumbnail this
            column rendered was a separate S3 request plus a decoded bitmap held for as long as
            the row stayed mounted, which is what the list was spending its scrolling on. The
            full-size media still loads in TiriPanel, once, for the one row that is open. */}
        <Text c1 center col={theme.GREY_70}>
          {mediaLabel}
        </Text>
      </Cell>

      <Cell>
        <BadgeRow>
          {modules.map((m) => {
            const style = MODULE_STYLES[m];
            if (!style) return null;
            return (
              <Badge key={m} bg={style.bg} col={style.col}>
                {t(`admin.tiri.modules.${m}`)}
              </Badge>
            );
          })}
        </BadgeRow>
      </Cell>

      <Cell>
        <Badge
          bg={item.source === 'direct' ? '#E1F5FE' : '#FFF8E1'}
          col={item.source === 'direct' ? '#01579B' : '#E65100'}
        >
          {t(`admin.tiri.sources.${item.source === 'direct' ? 'direct' : 'fancam'}`)}
        </Badge>
      </Cell>

      <Cell>
        <Text center maxWid="100%">
          {item.owner?.name || '-'}
        </Text>
        {item.owner?.username && (
          <Text c1 center maxWid="100%" col={theme.GREY_60}>
            @{item.owner.username}
          </Text>
        )}
      </Cell>

      <Cell>
        <Text center maxWid="100%">
          {item.team?.name || '-'}
        </Text>
      </Cell>

      <Cell>
        {/* A Rate It-only item has nothing to classify, so it is neither pending nor labeled —
            saying "Pending" would send admins looking for work that does not exist. */}
        {!isTaggable ? (
          <Text c1 center col={theme.GREY_60}>
            —
          </Text>
        ) : (
          <Text b2 bold center col={categoryStatus === 'LABELED' ? theme.GREEN : theme.SECONDARY}>
            {t(`admin.tiri.status.${categoryStatus === 'LABELED' ? 'labeled' : 'pending'}`)}
          </Text>
        )}
      </Cell>

      <Cell>
        {!isTaggable ? (
          <Text c1 center col={theme.GREY_60}>
            {t('admin.tiri.notApplicable')}
          </Text>
        ) : (
          <Text c1 center maxWid="100%">
            {category?.categoryName || '-'}
          </Text>
        )}
      </Cell>

      <Cell>
        {/* Labeling is only one of three gates — an item can be labeled and still not served
            while moderation is pending — so this is shown separately from the status above. */}
        <Text c1 center col={isPoolEligible ? theme.GREEN : theme.GREY_60}>
          {isTaggable ? t(`admin.tiri.feed.${isPoolEligible ? 'live' : 'notLive'}`) : '—'}
        </Text>
      </Cell>

      <Cell>
        <Text c1 center>
          {createdAt}
        </Text>
      </Cell>
    </Row>
  );
});

export default ListItem;
